// business-dashboard/src/hooks/useBlockchainAccess.ts
//
// Reads the current business's blockchain capability from the backend.
// Use anywhere in the business portal to show/hide blockchain UI.
//
// Usage:
//   const blockchain = useBlockchainAccess();
//   if (blockchain.subscribed) { ... }
//
// Returns:
//   - available    : server has BLOCKCHAIN_ENABLED=true
//   - subscribed   : customer's tier !== 'none'
//   - enabled      : customer toggled it on
//   - allowed      : ALL conditions met (use this for the actual gate)
//   - tier         : 'none' | 'basic' | 'premium' | 'enterprise'
//   - reason       : if not allowed, why (e.g. 'tier_not_subscribed')
//   - remainingAnchors : how many anchors left this month
//   - loading      : data still being fetched
//   - refresh()    : re-fetch from server (call after toggle/upgrade)

import { useEffect, useState, useCallback } from 'react';
import BusinessApiService from '../services/BusinessApiService';

const API_BASE = process.env.REACT_APP_API_URL || '';

export type BlockchainTier = 'none' | 'basic' | 'premium' | 'enterprise';

export interface BlockchainAccess {
  available: boolean;
  subscribed: boolean;
  enabled: boolean;
  allowed: boolean;
  tier: BlockchainTier;
  network: string;
  reason: string | null;
  monthlyAnchorLimit: number;
  monthlyAnchorsUsed: number;
  remainingAnchors: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useBlockchainAccess(): BlockchainAccess {
  const [state, setState] = useState<Omit<BlockchainAccess, 'refresh'>>({
    available: false,
    subscribed: false,
    enabled: false,
    allowed: false,
    tier: 'none',
    network: 'polygon-amoy',
    reason: null,
    monthlyAnchorLimit: 0,
    monthlyAnchorsUsed: 0,
    remainingAnchors: 0,
    loading: true,
  });

  const fetchStatus = useCallback(async () => {
    try {
      const token = BusinessApiService.getBusinessToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Get current user's profile (which now includes blockchain capability)
      const res = await fetch(`${API_BASE}/business-user/profile`, { headers });
      const data = await res.json();

      const cap = data?.data?.blockchain || data?.data?.capabilities?.blockchain || {};

      setState({
        available: !!cap.available,
        subscribed: !!cap.subscribed,
        enabled: !!cap.enabled,
        allowed: !!cap.available && !!cap.subscribed && !!cap.enabled,
        tier: (cap.tier as BlockchainTier) || 'none',
        network: cap.network || 'polygon-amoy',
        reason: cap.reason || null,
        monthlyAnchorLimit: cap.monthlyAnchorLimit || 0,
        monthlyAnchorsUsed: cap.monthlyAnchorsUsed || 0,
        remainingAnchors: cap.remainingAnchors || 0,
        loading: false,
      });
    } catch (err) {
      console.warn('[useBlockchainAccess] Failed to fetch:', err);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { ...state, refresh: fetchStatus };
}

// ─── Helper: human-readable reason messages ───
export const REASON_MESSAGES: Record<string, string> = {
  blockchain_disabled_globally:
    'Blockchain features are temporarily unavailable. Try again later.',
  tier_not_subscribed:
    "Upgrade to a blockchain plan to unlock tamper-proof verification.",
  feature_disabled_by_business:
    'You have paused blockchain anchoring. Re-enable it in settings.',
  business_not_approved:
    'Your business is pending approval. Blockchain features will activate once approved.',
  monthly_limit_reached:
    "You've reached this month's blockchain anchor limit. Upgrade for more.",
};

export function getBlockchainReasonMessage(reason: string | null): string {
  if (!reason) return '';
  return REASON_MESSAGES[reason] || 'Blockchain not available.';
}
